import { useEffect, useMemo, useState } from "react";
import airtable from "../airtables";
import { FriendsOfDwfPartner } from "../airtables/models";
import { partnerFieldIds } from "../airtables/tables/Partners";

const slugifyPartner = (name: string) =>
  name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

const buildPartnerLinks = (partner: FriendsOfDwfPartner) => {
  const links = [
    { url: partner[partnerFieldIds.link1], label: partner[partnerFieldIds.link1Text] },
    { url: partner[partnerFieldIds.link2], label: partner[partnerFieldIds.link2Text] },
    { url: partner[partnerFieldIds.link3], label: partner[partnerFieldIds.link3Text] },
  ];

  return links
    .filter(
      (link): link is { url: string; label: string } =>
        Boolean(link.url) && Boolean(link.label?.trim())
    )
    .map((link) => ({
      label: link.label.trim(),
      url: link.url,
    }));
};

export default function useFriendsOfDwf() {
  const [partners, setPartners] = useState<FriendsOfDwfPartner[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    airtable.friendsOfDwfPartners
      .getAll()
      .then((records: FriendsOfDwfPartner[]) => {
        setPartners(records);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const visiblePartners = useMemo(
    () => partners.filter((partner) => Boolean(partner[partnerFieldIds.show])),
    [partners]
  );

  const partnerSummaries = useMemo(
    () =>
      visiblePartners
        .filter((partner) => Boolean(partner[partnerFieldIds.name]))
        .map((partner) => ({
          id: partner.id ?? partner[partnerFieldIds.name] ?? "",
          name: partner[partnerFieldIds.name] ?? "",
          slug: slugifyPartner(partner[partnerFieldIds.name] ?? ""),
          logoUrl: partner[partnerFieldIds.logo]?.[0]?.url,
        })),
    [visiblePartners]
  );

  const partnerDetails = useMemo(() => {
    const map = new Map<string, FriendsOfDwfPartner>();
    visiblePartners.forEach((partner) => {
      const name = partner[partnerFieldIds.name]?.trim();
      if (name) {
        map.set(name, partner);
        map.set(slugifyPartner(name), partner);
      }
    });
    return map;
  }, [visiblePartners]);

  const getPartnerBySlug = (slug: string) => partnerDetails.get(slug);

  return {
    isLoading,
    partners: visiblePartners,
    partnerSummaries,
    buildPartnerLinks,
    getPartnerBySlug,
  };
}
